import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress } from '@mui/material';
import { 
    IconUsers, 
    IconPlant, 
    IconLeaf, 
    IconShieldCheck, 
    IconLayoutDashboard,
    IconChartLine,
    IconChevronRight,
    IconClock
} from '@tabler/icons-react';
import { fonts, colors } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card sx={{ 
        height: '100%', 
        borderRadius: '24px', 
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
        border: '1px solid #F1F5F9',
        overflow: 'hidden'
    }}>
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ 
                    p: 1.5, 
                    borderRadius: '16px', 
                    backgroundColor: color + '15',
                    color: color 
                }}>
                    <Icon size={28} />
                </Box>
                <Typography sx={{ 
                    fontFamily: fonts.poppins, 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: '#64748B' 
                }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ 
                fontFamily: fonts.poppins, 
                fontSize: '32px', 
                fontWeight: 800, 
                color: '#1E293B' 
            }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const QuickAction = ({ icon: Icon, title, description, color, onClick }) => (
    <Box 
        onClick={onClick}
        sx={{ 
            p: 3, 
            borderRadius: '24px', 
            backgroundColor: '#FFFFFF',
            border: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.08)',
                borderColor: color
            }
        }}
    >
        <Box sx={{ 
            width: '60px', 
            height: '60px', 
            borderRadius: '18px', 
            backgroundColor: color + '15', 
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Icon size={32} />
        </Box>
        <Box sx={{ flex: 1 }}>
            <Typography sx={{ 
                fontFamily: fonts.poppins, 
                fontSize: '18px', 
                fontWeight: 700, 
                color: '#1E293B' 
            }}>
                {title}
            </Typography>
            <Typography sx={{ 
                fontFamily: fonts.poppins, 
                fontSize: '14px', 
                color: '#64748B' 
            }}>
                {description}
            </Typography>
        </Box>
        <IconChevronRight size={24} color="#CBD5E1" />
    </Box>
);

const AdminHome = () => {
    const { t } = useTranslation(['dashboard']);
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${url}/api/admin/stats`);
                const data = await response.json();
                setStats(data);
            } catch (err) {
                console.error("Error fetching stats:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress color="success" />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1400px', margin: '0 auto' }}>
            <Box sx={{ mb: 6 }}>
                <Typography sx={{ 
                    fontFamily: fonts.poppins, 
                    fontWeight: 800, 
                    fontSize: { xs: '28px', md: '36px' }, 
                    color: '#1E293B',
                    mb: 1
                }}>
                    {t('dashboard:adminOverviewHeader')}
                </Typography>
                <Typography sx={{ 
                    fontFamily: fonts.poppins, 
                    fontSize: '16px', 
                    color: '#64748B' 
                }}>
                    {t('dashboard:adminOverviewSub')}
                </Typography>
            </Box>

            {/* Statistics Grid */}
            <Grid container spacing={3} sx={{ mb: 8 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        icon={IconUsers} 
                        title={t('dashboard:totalSellers')} 
                        value={stats?.totalSellers || 0} 
                        color={colors.primary} 
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        icon={IconUsers} 
                        title={t('dashboard:totalBuyers')} 
                        value={stats?.totalBuyers || 0} 
                        color="#3B82F6" 
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        icon={IconPlant} 
                        title={t('dashboard:totalCrops')} 
                        value={stats?.totalCrops || 0} 
                        color="#F59E0B" 
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        icon={IconClock} 
                        title={t('dashboard:pendingProofs')} 
                        value={stats?.pendingProofs || 0} 
                        color="#EF4444" 
                    />
                </Grid>
            </Grid>

            {/* Quick Actions */}
            <Typography sx={{ 
                fontFamily: fonts.poppins, 
                fontWeight: 700, 
                fontSize: '22px', 
                color: '#1E293B',
                mb: 3 
            }}>
                {t('dashboard:managementShortcuts')}
            </Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <QuickAction 
                        icon={IconLeaf}
                        title={t('dashboard:verifyCarbonProofs')}
                        description={t('dashboard:verifyCarbonDesc')}
                        color="#10B981"
                        onClick={() => navigate('/manage-carbon-proofs')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <QuickAction 
                        icon={IconShieldCheck}
                        title={t('dashboard:manageCategories')}
                        description={t('dashboard:manageCategoriesDesc')}
                        color="#6366F1"
                        onClick={() => navigate('/manage-crops')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <QuickAction 
                        icon={IconChartLine}
                        title={t('dashboard:managePlans')}
                        description={t('dashboard:managePlansDesc')}
                        color="#F59E0B"
                        onClick={() => navigate('/manage-plans')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <QuickAction 
                        icon={IconLayoutDashboard}
                        title={t('dashboard:dashboardBanners')}
                        description={t('dashboard:dashboardBannersDesc')}
                        color="#3B82F6"
                        onClick={() => navigate('/manage-dashboard')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdminHome;
